/**
 * One ribbon, drawn as SVG stripes (docs/Decisions.md D42;
 * .claude/design-system/design-rules.md "Ribbons and badges").
 *
 * Sized `w-ribbon-w h-ribbon-h` with `rounded-dcon-sm` corners. Token-named
 * stripes fill with `var(--<token>)`; an organization's own `#RRGGBB` stripe
 * colours are content and go into the SVG `fill` from data. The level device
 * (star, oak leaf, numeral) sits centered in a `medal-*` colour, and the
 * level word is always in the accessible name and tooltip.
 */
import { cn } from '../../lib/ui';
import { colorFill, stripeRects, ribbonLabel, ribbonTooltip } from '../../lib/ribbons';

const W = 44;
const H = 14;

function starPoints(cx, cy, outer, inner) {
  const points = [];
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    points.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return points.join(' ');
}

export function RibbonDevice({ device, level, width = W, height = H }) {
  if (!device || !device.type || device.type === 'none') return null;
  const fill = colorFill(device.color || 'medal-gold');
  const cx = width / 2;
  const cy = height / 2;
  if (device.type === 'star') {
    return (
      <polygon
        points={starPoints(cx, cy, height * 0.4, height * 0.17)}
        fill={fill}
        stroke="var(--deep)"
        strokeWidth="0.4"
      />
    );
  }
  if (device.type === 'oak-leaf') {
    const w = height * 0.75;
    return (
      <g stroke="var(--deep)" strokeWidth="0.4">
        <path
          d={`M${cx - w} ${cy} C${cx - w / 2} ${cy - height * 0.35} ${cx + w / 2} ${cy - height * 0.35} ${cx + w} ${cy} C${cx + w / 2} ${cy + height * 0.35} ${cx - w / 2} ${cy + height * 0.35} ${cx - w} ${cy} Z`}
          fill={fill}
        />
        <line x1={cx - w} y1={cy} x2={cx + w} y2={cy} />
      </g>
    );
  }
  if (device.type === 'numeral') {
    const n = level && Number.isInteger(level.index) ? level.index + 1 : 1;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={height * 0.72}
        fontWeight="700"
        fill={fill}
        stroke="var(--deep)"
        strokeWidth="0.5"
        paintOrder="stroke"
      >
        {n}
      </text>
    );
  }
  return null;
}

/**
 * @param {{ item: object, showLevel?: boolean, showScope?: boolean, className?: string }} props
 *   item: a rack item (RackItem in integration/openapi.yaml) or a designer preview.
 */
export default function RibbonBar({ item, showLevel = true, showScope = false, className }) {
  const rects = stripeRects(item.design && item.design.stripes, W);
  const label = showLevel ? ribbonLabel(item) : item.name;
  const tooltip = ribbonTooltip(showLevel ? item : { ...item, level: null }, { showScope });
  return (
    <span
      className={cn(
        'inline-block w-ribbon-w h-ribbon-h shrink-0 overflow-hidden rounded-dcon-sm shadow-dcon-sm',
        item.hidden && 'opacity-55',
        className
      )}
      title={tooltip}
    >
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block size-full"
      >
        <title>{tooltip}</title>
        {rects.map((rect, index) => (
          <rect
            key={index}
            x={rect.x}
            y="0"
            width={rect.width + 0.05}
            height={H}
            fill={rect.fill}
          />
        ))}
        {showLevel && (
          <RibbonDevice device={item.design && item.design.device} level={item.level} />
        )}
      </svg>
    </span>
  );
}
